/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../store/rootReducer';
import { SeedCartState, SeedMix, SeedOrdersInfo } from '../types/SeedOrderFormType';
import { DeliveryAddress } from '../../cart/storage/cart.types';
import { getSeedCart, getSeedCartById, syncSeedCart } from './seedCartThunk';
import _ from 'lodash';

export const initialState: SeedCartState = {
  loading: false,
  seedOrder: {},
  syncing: false,
  syncedAt: {},
  updatedAt: {},
  errors: {},
};

const updateSeedOrder = (
  state: SeedCartState,
  { payload }: { payload: SeedOrdersInfo | undefined },
) => {
  if (payload && state.seedOrder && payload.accountNumber) {
    const currentMixes = _.cloneDeep(state.seedOrder[payload.accountNumber]?.mixes);

    const newPayload = {
      ...payload,
      mixes: payload.mixes.map((mix, index) => ({
        ...mix,
        products: mix.products.map((product, index2) => ({
          ...product,
          quantityRoundedUp: _.get(
            currentMixes,
            `[${index}].products[${index2}].quantityRoundedUp`,
          ),
        })),
        paddocksExpanded: _.get(currentMixes, `[${index}].paddocksExpanded`, true),
        productsExpanded: _.get(currentMixes, `[${index}].productsExpanded`, true),
        notesExpanded: _.get(currentMixes, `[${index}].notesExpanded`, false),
      })),
    };

    state.seedOrder[payload.accountNumber] = _.cloneDeep(newPayload);
    if (!state.updatedAt) state.updatedAt = {};
    state.updatedAt[payload.accountNumber] = new Date();
  }
};

const SeedCartSlice = createSlice({
  name: 'seedCart',
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(syncSeedCart.pending, (state) => {
      state.syncing = true;
    });
    builder.addCase(syncSeedCart.fulfilled, (state, { payload }) => {
      if (payload?.accountNumber) {
        updateSeedOrder(state, { payload });
        if (!state.syncedAt) state.syncedAt = {};
        state.syncedAt[payload.accountNumber] = new Date();
      }
      state.syncing = false;
    });
    builder.addCase(syncSeedCart.rejected, (state) => {
      state.syncing = false;
    });
    builder.addCase(getSeedCartById.fulfilled, (state, { payload }) => {
      if (payload && state.seedOrder && payload.accountNumber) {
        updateSeedOrder(state, { payload });
        if (!state.updatedAt) state.updatedAt = {};
        state.updatedAt[payload.accountNumber] = new Date();
      }
      state.loading = false;
      state.syncing = false;
    });
    builder.addCase(getSeedCart.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSeedCart.fulfilled, (state, { payload }) => {
      if (payload && state.seedOrder && payload.accountNumber) {
        updateSeedOrder(state, { payload });
        if (!state.updatedAt) state.updatedAt = {};
        state.updatedAt[payload.accountNumber] = new Date();
      }
      state.loading = false;
      state.syncing = false;
    });
    builder.addCase(getSeedCart.rejected, (state) => {
      state.loading = false;
    });
  },
  reducers: {
    setStoreIdAndDeliveryAddress(
      state: SeedCartState,
      {
        payload: { accountNumber, mixId, storeId, deliveryAddress },
      }: PayloadAction<{
        accountNumber: string;
        mixId: string;
        storeId: string | null | undefined;
        deliveryAddress: DeliveryAddress | null | undefined;
      }>,
    ) {
      const mixes = state.seedOrder?.[accountNumber]?.mixes;
      if (mixes) {
        const mix = mixes.find((m) => m.id === mixId);
        if (mix) {
          mix.storeId = storeId;
          mix.deliveryAddress = deliveryAddress;
        }
      }
    },
    emptySeedOrder(
      state: SeedCartState,
      { payload: { accountNumber } }: PayloadAction<{ accountNumber: string }>,
    ) {
      state.loading = false;
      delete state.seedOrder?.[accountNumber];
      state.syncing = false;
      delete state.syncedAt?.[accountNumber];
      delete state.updatedAt?.[accountNumber];
    },
    setSeedValue(
      state: SeedCartState,
      {
        payload: { accountNumber, seedMix, key, value },
      }: PayloadAction<{ accountNumber: string; seedMix: SeedMix; key: string; value: unknown }>,
    ) {
      // Ensure the seed order for the account number exists
      if (!state.seedOrder[accountNumber]) {
        state.seedOrder[accountNumber] = {} as SeedOrdersInfo;
      }

      const copy = _.cloneDeep(seedMix);
      const indexOf = state.seedOrder[accountNumber].mixes.findIndex(
        (seeds) => seeds.id === seedMix.id,
      );

      if (key === '') {
        state.seedOrder[accountNumber].mixes[indexOf] = value as SeedMix;
      } else {
        _.set(copy, key, value);
        state.seedOrder[accountNumber].mixes[indexOf] = copy;
      }
    },
    removeSeedMix(
      state: SeedCartState,
      {
        payload: { accountNumber, seedMix },
      }: PayloadAction<{ accountNumber: string; seedMix: SeedMix }>,
    ) {
      // Ensure the seed order for the account number exists
      if (state.seedOrder[accountNumber]) {
        if (seedMix.id) {
          const mixes = state.seedOrder[accountNumber].mixes;
          const indexOfMix = mixes.findIndex((mix) => mix.id === seedMix.id);

          if (indexOfMix !== -1) {
            // Remove the seed mix with the matching id
            mixes.splice(indexOfMix, 1);
            state.seedOrder[accountNumber].mixes = mixes;
          }
        } else {
          const mixes = state.seedOrder[accountNumber].mixes;
          const indexOfMix = mixes.findIndex((mix) => mix.name === seedMix.name);

          if (indexOfMix !== -1) {
            // Remove the seed mix with the matching id
            mixes.splice(indexOfMix, 1);
            state.seedOrder[accountNumber].mixes = mixes;
          }
        }
      }
    },
    insertSeedMixAfter(
      state: SeedCartState,
      {
        payload: { accountNumber, seedMixA, seedMixB },
      }: PayloadAction<{ accountNumber: string; seedMixA?: SeedMix; seedMixB: SeedMix }>,
    ) {
      // Ensure the seed order for the account number exists
      if (!state.seedOrder[accountNumber]) {
        state.seedOrder[accountNumber] = {} as SeedOrdersInfo;
      }

      if (seedMixA) {
        const mixes = state.seedOrder[accountNumber].mixes;
        const indexOfA = mixes.findIndex((mix) => mix.id === seedMixA.id);

        if (indexOfA !== -1) {
          // Insert seedMixB immediately after seedMixA
          mixes.splice(indexOfA + 1, 0, seedMixB);
        } else {
          // If seedMixA is not found, optionally handle the case (e.g., append to the end)
          mixes.push(seedMixB);
        }
      } else {
        state.seedOrder[accountNumber].mixes = [...state.seedOrder[accountNumber].mixes, seedMixB];
      }
    },
    setSeedOrder(
      state: SeedCartState,
      {
        payload: { accountNumber, seedOrder },
      }: PayloadAction<{ accountNumber: string; seedOrder: SeedOrdersInfo }>,
    ) {
      state.loading = false;
      if (!state.seedOrder) state.seedOrder = {};
      state.seedOrder[accountNumber] = seedOrder;
    },
    setError(
      state: SeedCartState,
      {
        payload: { accountNumber, message },
      }: PayloadAction<{ accountNumber: string; message: string | undefined }>,
    ) {
      state.loading = false;
      if (!state.errors) state.errors = {};
      state.errors[accountNumber] = message;
    },
  },
});

export const selectState = (state: RootState) => state.seedCart;

export const selectSeedOrder = (accountNumber?: string) => {
  return createSelector(selectState, (state) => {
    if (accountNumber && state.seedOrder[accountNumber]) {
      return state.seedOrder[accountNumber];
    }

    return undefined;
  });
};
export const selectSeedCartSyncedAt = (accountNumber: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (accountNumber && state.syncedAt) return state.syncedAt[accountNumber];
  });
};
export const selectSeedCartUpdatedAt = (accountNumber: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (accountNumber && state.updatedAt) return state.updatedAt[accountNumber];
  });
};
export const selectSeedCartError = (accountNumber: string | undefined) => {
  return createSelector(selectState, (state) => {
    if (accountNumber && state.errors) return state.errors[accountNumber];
  });
};
export const selectSeedOrderLoading = createSelector(selectState, (state) => state.loading);
export const selectSeedOrderSyncing = createSelector(selectState, (state) => state.syncing);

export const { setSeedValue, insertSeedMixAfter, removeSeedMix } = SeedCartSlice.actions;
export default SeedCartSlice;
